import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, required } from '@angular/forms/signals';
import {
  CreateFeatureRequestV1,
  DiscountType,
  FeatureResponseV1,
  FeatureType,
  PricingFrequency,
  ProductLine,
  UsageFrequency
} from '@membership/membership';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';

import { DialogMode } from '@core/models/models';
import { MembershipService } from '@core/services/membership';

interface FeatureFormModel {
  name: string;
  description: string;
  categoryCode: string;
  shorthandCode: string;
  featureType: FeatureType;
  baselinePrice: number;
  productLine: ProductLine;
  oneTimeFee: number | null;
  inventory: number;
  pricingFrequency: PricingFrequency;
  usageFrequency: UsageFrequency;
  discountRate: number;
  discountType: DiscountType;
  isGroupLevel: boolean;
  isActive: boolean;
}

@Component({
  selector: 'app-features-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    DialogModule,
    SelectModule,
    IconField,
    InputIcon,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    FormField,
    ProgressSpinnerModule
  ],
  templateUrl: './features-table.html',
  styleUrl: './features-table.scss'
})
export class FeaturesTableComponent {
  private readonly membershipService = inject(MembershipService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly featuresResource = this.membershipService.createFeaturesResource();

  readonly searchTerm = signal<string>('');
  readonly showDialog = signal<boolean>(false);
  readonly dialogMode = signal<DialogMode>(DialogMode.Create);
  readonly selectedFeatureId = signal<string | null>(null);
  readonly deletingId = signal<string | null>(null);
  readonly saving = signal<boolean>(false);

  readonly featureFormModel = signal<FeatureFormModel>(this.emptyFeatureForm());

  readonly featureForm = form(this.featureFormModel, (schemaPath) => {
    required(schemaPath.name, { message: 'Feature name is required' });
    required(schemaPath.categoryCode, { message: 'Category code is required' });
    required(schemaPath.shorthandCode, { message: 'Shorthand code is required' });
  });

  readonly featureTypeOptions = signal([
    { label: 'Basic', value: FeatureType.Basic },
    { label: 'Experience', value: FeatureType.Experience }
  ]);

  readonly productLineOptions = signal([
    { label: 'Marketplace', value: ProductLine.Marketplace },
    { label: 'Health', value: ProductLine.Health }
  ]);

  readonly pricingFrequencyOptions = signal([
    { label: 'Once', value: PricingFrequency.Once },
    { label: 'Startup', value: PricingFrequency.Startup },
    { label: 'Monthly', value: PricingFrequency.Monthly },
    { label: 'Quarterly', value: PricingFrequency.Quarterly },
    { label: 'Semi-Annual', value: PricingFrequency.SemiAnnual },
    { label: 'Annual', value: PricingFrequency.Annual }
  ]);

  readonly usageFrequencyOptions = signal([
    { label: 'Static', value: UsageFrequency.Static },
    { label: 'Unlimited', value: UsageFrequency.Unlimited },
    { label: 'Monthly', value: UsageFrequency.Monthly },
    { label: 'Quarterly', value: UsageFrequency.Quarterly },
    { label: 'Semi-Annual', value: UsageFrequency.SemiAnnual },
    { label: 'Annual', value: UsageFrequency.Annual }
  ]);

  readonly discountTypeOptions = signal([
    { label: 'Percentage', value: DiscountType.Percentage },
    { label: 'Flat', value: DiscountType.Flat }
  ]);

  readonly columns = signal<{ field: string; header: string; sortable: boolean }[]>([
    { field: 'name', header: 'Feature Name', sortable: true },
    { field: 'featureCode', header: 'Code', sortable: true },
    { field: 'description', header: 'Description', sortable: false },
    { field: 'featureType', header: 'Type', sortable: true },
    { field: 'baselinePrice', header: 'Price', sortable: true },
    { field: 'oneTimeFee', header: 'OTF', sortable: true },
    { field: 'isActive', header: 'Status', sortable: true },
    { field: 'createdOn', header: 'Created', sortable: true },
    { field: 'actions', header: 'Actions', sortable: false }
  ]);

  readonly filteredFeatures = computed<FeatureResponseV1[]>(() => {
    const searchTerm = this.searchTerm().toLowerCase();
    const features: FeatureResponseV1[] = this.featuresResource.value() ?? [];
    if (!searchTerm) return features;

    return features.filter(
      (feature: FeatureResponseV1) =>
        feature.name.toLowerCase().includes(searchTerm) || feature.description?.toLowerCase().includes(searchTerm)
    );
  });

  readonly dialogTitle = computed(() => (this.dialogMode() === DialogMode.Create ? 'Create Feature' : 'Edit Feature'));

  readonly dialogButtonLabel = computed(() => (this.dialogMode() === DialogMode.Create ? 'Create' : 'Update'));

  readonly isLoading = computed(() => this.featuresResource.isLoading());

  readonly selectedFeatureCode = computed<string | null>(() => {
    const id = this.selectedFeatureId();
    if (!id) return null;
    const features = this.featuresResource.value() ?? [];
    return features.find((f) => f.featureId === id)?.featureCode ?? null;
  });

  readonly isEditMode = computed(() => this.dialogMode() === DialogMode.Edit);

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  onCreateFeature(): void {
    this.dialogMode.set(DialogMode.Create);
    this.selectedFeatureId.set(null);
    this.featureFormModel.set(this.emptyFeatureForm());
    this.showDialog.set(true);
  }

  onEditFeature(feature: FeatureResponseV1): void {
    this.dialogMode.set(DialogMode.Edit);
    this.selectedFeatureId.set(feature.featureId);
    this.featureFormModel.set({
      name: feature.name,
      description: feature.description || '',
      categoryCode: feature.categoryCode,
      shorthandCode: feature.shorthandCode,
      featureType: feature.featureType,
      baselinePrice: feature.baselinePrice,
      productLine: feature.productLine,
      oneTimeFee: feature.oneTimeFee ?? null,
      inventory: feature.inventory,
      pricingFrequency: feature.pricingFrequency,
      usageFrequency: feature.usageFrequency,
      discountRate: feature.discountRate ?? 0,
      discountType: feature.discountType ?? DiscountType.Percentage,
      isGroupLevel: feature.isGroupLevel ?? false,
      isActive: feature.isActive
    });
    this.showDialog.set(true);
  }

  async onSaveFeature(): Promise<void> {
    if (!this.isFormValid()) {
      this.showError('Please fix validation errors');
      return;
    }

    this.saving.set(true);
    const formData = this.featureFormModel();
    const payload: CreateFeatureRequestV1 = {
      name: formData.name,
      description: formData.description || undefined,
      categoryCode: formData.categoryCode,
      shorthandCode: formData.shorthandCode,
      featureType: formData.featureType,
      baselinePrice: formData.baselinePrice,
      productLine: formData.productLine,
      oneTimeFee: formData.oneTimeFee ?? undefined,
      inventory: formData.inventory,
      pricingFrequency: formData.pricingFrequency,
      usageFrequency: formData.usageFrequency,
      discountRate: formData.discountRate,
      discountType: formData.discountType,
      isGroupLevel: formData.isGroupLevel,
      isActive: formData.isActive
    };

    const operation =
      this.dialogMode() === DialogMode.Create
        ? this.membershipService.createFeature(payload)
        : this.membershipService.updateFeature(this.selectedFeatureId()!, payload as Partial<FeatureResponseV1>);

    try {
      await firstValueFrom(operation);
      this.showSuccess(`Feature ${this.dialogMode() === DialogMode.Create ? 'created' : 'updated'} successfully`);
      this.showDialog.set(false);
      this.featuresResource.reload();
    } catch (error) {
      this.showError('Failed to save feature');
      console.error('Save feature error:', error);
    } finally {
      this.saving.set(false);
    }
  }

  onDeleteFeature(feature: FeatureResponseV1): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete feature "${feature.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteFeature(feature)
    });
  }

  private async deleteFeature(feature: FeatureResponseV1): Promise<void> {
    this.deletingId.set(feature.featureId);

    try {
      await firstValueFrom(this.membershipService.deleteFeature(feature.featureId));
      this.showSuccess(`Feature ${feature.name} deleted successfully`);
      this.featuresResource.reload();
    } catch (error) {
      this.showError('Failed to delete feature');
      console.error('Delete feature error:', error);
    } finally {
      this.deletingId.set(null);
    }
  }

  isFormValid(): boolean {
    const model = this.featureFormModel();
    return !!(
      model.name.trim() &&
      model.categoryCode.trim() &&
      model.shorthandCode.trim() &&
      model.baselinePrice >= 0 &&
      model.inventory >= 1 &&
      this.featureForm.name().valid() &&
      this.featureForm.categoryCode().valid() &&
      this.featureForm.shorthandCode().valid()
    );
  }

  onFeatureTypeChange(featureType: FeatureType) {
    this.featureFormModel.set({
      ...this.featureFormModel(),
      featureType
    });
  }

  onProductLineChange(productLine: ProductLine) {
    this.featureFormModel.set({
      ...this.featureFormModel(),
      productLine
    });
  }

  onPricingFrequencyChange(pricingFrequency: PricingFrequency) {
    this.featureFormModel.set({
      ...this.featureFormModel(),
      pricingFrequency
    });
  }

  onUsageFrequencyChange(usageFrequency: UsageFrequency) {
    this.featureFormModel.set({
      ...this.featureFormModel(),
      usageFrequency
    });
  }

  onDiscountTypeChange(discountType: DiscountType) {
    this.featureFormModel.set({
      ...this.featureFormModel(),
      discountType
    });
  }

  getFeatureTypeLabel(type: FeatureType): string {
    return type === FeatureType.Basic ? 'Basic' : 'Experience';
  }

  getFeatureTypeSeverity(type: FeatureType): 'info' | 'secondary' {
    return type === FeatureType.Basic ? 'secondary' : 'info';
  }

  getStatusSeverity(isActive: boolean): 'success' | 'danger' {
    return isActive ? 'success' : 'danger';
  }

  getStatusLabel(isActive: boolean): string {
    return isActive ? 'Active' : 'Inactive';
  }

  formatCurrency(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  }

  private emptyFeatureForm(): FeatureFormModel {
    return {
      name: '',
      description: '',
      categoryCode: '',
      shorthandCode: '',
      featureType: FeatureType.Basic,
      baselinePrice: 0,
      productLine: ProductLine.Marketplace,
      oneTimeFee: null,
      inventory: 1,
      pricingFrequency: PricingFrequency.Once,
      usageFrequency: UsageFrequency.Static,
      discountRate: 0,
      discountType: DiscountType.Percentage,
      isGroupLevel: false,
      isActive: true
    };
  }

  private showError(message: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
  }

  private showSuccess(message: string): void {
    this.messageService.add({ severity: 'success', summary: 'Success', detail: message });
  }
}
